import { createFileRoute } from "@tanstack/react-router";
import { Table, Tag, Typography, Flex, Button, Modal, Form, App, theme, Switch, Select, Spin, Input } from "antd";
import type { ColumnsType, TableProps } from "antd/es/table";
import { PlusOutlined, LaptopOutlined, SearchOutlined, ClearOutlined } from "@ant-design/icons";
import { useState, useEffect, useMemo } from "react";
import { z } from "zod";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import duration from "dayjs/plugin/duration";
import {
  useQ_PageSessions_List,
  type PageSessions_List_Row,
  type SessionsSortField,
  type SessionsSortOrder,
} from "@/hooks/Sessions/useQ_PageSessions_List";
import { useQ_PageSettings_Organization } from "@/hooks/OrgSettings/useQ_PageSettings_Organization";
import { useM_PageSessions_SessionCreate } from "@/hooks/Sessions/useM_PageSessions_SessionCreate";
import { useQ_PageReadiness_Profiles } from "@/hooks/Readiness/useQ_PageReadiness_Profiles";
import { App_SessionComposer, type SessionComposerValues, flattenToFormValues, US_STATES } from "@/components/App_SessionComposer/App_SessionComposer";
import { supabase } from "@/configs/supabase/config";
import { const_SessionTypeOptions } from "@/hooks/Sessions/const_SessionTypeOptions";
import { deriveSessionStatus } from "@/utils/utils_Sessions_DerivedStatus";

dayjs.extend(relativeTime);
dayjs.extend(duration);

function fmtDur(ms: number): string {
  const d = dayjs.duration(ms);
  const mins = Math.floor(d.asMinutes());
  const secs = d.seconds();
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
}

type SessionRow = PageSessions_List_Row;

const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });

type RateResultShape = { downPayment?: number; totalPremium?: number };
const rateOf = (record: SessionRow): RateResultShape => (record.rate_result as RateResultShape | null) ?? {};
const downOf = (record: SessionRow): number => rateOf(record).downPayment ?? 0;
const premiumOf = (record: SessionRow): number => record.premium ?? rateOf(record).totalPremium ?? 0;

const OUTCOME_FILTERS = [
  { text: "Active", value: "active" },
  { text: "Completed", value: "completed" },
  { text: "Early Termination", value: "early_termination" },
  { text: "Error", value: "failed" },
];

const STATE_FILTERS = US_STATES.map((s) => ({ text: s.value, value: s.value }));

// ---- URL-backed table state (single source of truth for the server query) ----
const SORT_FIELDS = ["created", "down", "premium", "outstanding"] as const;
const SORT_ORDERS = ["ascend", "descend"] as const;
const strArray = z.array(z.string()).optional().catch(undefined);

const SessionsSearchSchema = z.object({
  page: z.coerce.number().int().min(1).optional().catch(undefined),
  pageSize: z.coerce.number().int().min(1).optional().catch(undefined),
  sortField: z.enum(SORT_FIELDS).optional().catch(undefined),
  sortOrder: z.enum(SORT_ORDERS).optional().catch(undefined),
  session_type: strArray,
  status: strArray,
  mode: strArray,
  rate_state: strArray,
  license_state: strArray,
  // Presence means on. Deliberately NOT z.coerce.boolean(), which turns the
  // string "false" into true — a hand-edited URL would silently invert it.
  has_outstanding: z.enum(["true"]).optional().catch(undefined),
  q: z.string().optional().catch(undefined),
});

export const Route = createFileRoute("/_protected/$organizationId/$flowId/sessions/")({
  validateSearch: (search: Record<string, unknown>) => SessionsSearchSchema.parse(search),
  component: Page_Sessions,
});

function Page_Sessions() {
  const { token } = theme.useToken();
  const { message, modal } = App.useApp();
  const { organizationId, flowId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const page = search.page ?? 1;
  const pageSize = search.pageSize ?? 20;
  const sortField: SessionsSortField = search.sortField ?? "created";
  const sortOrder: SessionsSortOrder = search.sortOrder ?? "descend";
  const hasOutstandingOnly = search.has_outstanding === "true";
  const hasActiveFilters = !!(
    search.session_type?.length ||
    search.status?.length ||
    search.mode?.length ||
    search.rate_state?.length ||
    search.license_state?.length ||
    hasOutstandingOnly ||
    (search.q ?? "").length
  );
  const qOrganization = useQ_PageSettings_Organization({ organizationId });
  const mSessionCreate = useM_PageSessions_SessionCreate({ organizationId, flowSlug: flowId });
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [localBrowser, setLocalBrowser] = useState(() => localStorage.getItem("ihub:localBrowser") === "true");
  const [createForm] = Form.useForm();
  const [flowDbId, setFlowDbId] = useState<string | null>(null);

  // Resolve the flow slug → id once; both the sessions and profiles queries need it.
  useEffect(() => {
    if (flowDbId) return;
    let active = true;
    supabase.from("flows").select("id").eq("slug", flowId).single().then(({ data }) => {
      if (active && data) setFlowDbId(data.id);
    });
    return () => { active = false; };
  }, [flowId, flowDbId]);

  const qSessions = useQ_PageSessions_List({
    organizationId,
    flowDbId: flowDbId ?? "",
    page,
    pageSize,
    sortField,
    sortOrder,
    filters: {
      session_type: search.session_type,
      status: search.status,
      mode: search.mode,
      rate_state: search.rate_state,
      license_state: search.license_state,
      has_outstanding: hasOutstandingOnly,
    },
    search: search.q ?? "",
  });

  // Search box: keep an immediate local value for a responsive input, debounce it
  // into the URL `q` param (which drives the DB query), and reset to page 1.
  const [searchInput, setSearchInput] = useState(search.q ?? "");
  useEffect(() => { setSearchInput(search.q ?? ""); }, [search.q]);
  useEffect(() => {
    const t = setTimeout(() => {
      const next = searchInput.trim();
      if (next !== (search.q ?? "")) {
        navigate({ search: (prev) => ({ ...prev, q: next || undefined, page: undefined }) });
      }
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput, search.q, navigate]);

  const qProfiles = useQ_PageReadiness_Profiles({ organizationId, flowId: flowDbId ?? "" });

  const profileOptions = useMemo(() =>
    qProfiles.profiles.map((p) => {
      const name = [p.first_name, p.last_name].filter(Boolean).join(" ");
      const display = p.label || name || p.key;
      return {
        value: p.id,
        label: `${p.key} — ${display}`,
        searchText: [p.key, p.label, p.first_name, p.last_name].filter(Boolean).join(" ").toLowerCase(),
      };
    }),
    [qProfiles.profiles],
  );

  const handleCreateSession = async (values: SessionComposerValues) => {
    try {
      await mSessionCreate.mutation.mutateAsync({ values, localBrowser });
      message.success("Session created");
      setCreateModalOpen(false);
      createForm.resetFields();
    } catch {
      message.error("Failed to create session");
    }
  };

  // Push AntD pagination / filter / sort changes into the URL (→ refetch).
  // Filter or sort changes reset to page 1; a bare page change does not.
  const handleTableChange: TableProps<SessionRow>["onChange"] = (pagination, filters, sorter, extra) => {
    const single = Array.isArray(sorter) ? sorter[0] : sorter;
    const sorted = single && single.order;
    const resetPage = extra.action === "filter" || extra.action === "sort";
    navigate({
      search: (prev) => ({
        ...prev,
        page: resetPage ? undefined : pagination.current,
        pageSize: pagination.pageSize,
        sortField: sorted ? (single.columnKey as SessionsSortField) : undefined,
        sortOrder: sorted ? (single.order as SessionsSortOrder) : undefined,
        session_type: (filters.session_type as string[] | null) ?? undefined,
        status: (filters.status as string[] | null) ?? undefined,
        mode: (filters.mode as string[] | null) ?? undefined,
        rate_state: (filters.rate_state as string[] | null) ?? undefined,
        license_state: (filters.license_state as string[] | null) ?? undefined,
      }),
    });
  };

  const sortOrderFor = (field: SessionsSortField): SessionsSortOrder | null =>
    sortField === field ? sortOrder : null;

  const columns: ColumnsType<SessionRow> = [
    {
      title: "ID",
      dataIndex: "id",
      render: (id: string) => (
        <Typography.Text copyable={{ text: id }} style={{ fontFamily: "monospace", fontSize: token.fontSizeSM }}>
          {id.slice(0, 12)}...
        </Typography.Text>
      ),
    },
    {
      title: "Rate State",
      dataIndex: "rate_state",
      filters: STATE_FILTERS,
      filterSearch: true,
      filteredValue: search.rate_state ?? null,
      render: (s: string | null) => s ? <Typography.Text>{s}</Typography.Text> : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: "License State",
      dataIndex: "license_state",
      filters: STATE_FILTERS,
      filterSearch: true,
      filteredValue: search.license_state ?? null,
      render: (s: string | null) => s ? <Typography.Text>{s}</Typography.Text> : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: "Name",
      key: "name",
      render: (_: unknown, record: SessionRow) => {
        const name = [record.first_name, record.last_name].filter(Boolean).join(" ");
        return name ? <Typography.Text>{name}</Typography.Text> : <Typography.Text type="secondary">—</Typography.Text>;
      },
    },
    {
      title: "Type",
      dataIndex: "session_type",
      filters: [{ text: "Real", value: "real" }, { text: "Test", value: "test" }],
      filteredValue: search.session_type ?? null,
      render: (type: string) => {
        const meta = const_SessionTypeOptions.map[type];
        return meta ? <Tag color={meta.color}>{meta.label}</Tag> : <Tag>{type}</Tag>;
      },
    },
    {
      title: "Status",
      key: "status",
      filters: OUTCOME_FILTERS,
      filteredValue: search.status ?? null,
      render: (_: unknown, record: SessionRow) => {
        const status = deriveSessionStatus(record);
        return <Tag color={status.color}>{status.label}</Tag>;
      },
    },
    {
      title: "Reason",
      dataIndex: "end_code",
      render: (endCode: string | null) => endCode ? <Typography.Text type="secondary">{endCode.replace(/_/g, " ")}</Typography.Text> : "—",
    },
    {
      title: "Alive",
      dataIndex: "is_alive",
      render: (alive: string) => <Tag color={alive === "stopped" ? "default" : "processing"}>{alive}</Tag>,
    },
    {
      title: "Mode",
      dataIndex: "mode",
      filters: [
        { text: "Rate Check", value: "rate-check" },
        { text: "Generate Policy", value: "generate-policy" },
      ],
      filteredValue: search.mode ?? null,
      render: (mode: string | null) => mode ?? "—",
    },
    {
      title: "Down",
      key: "down",
      align: "right",
      sorter: true,
      sortOrder: sortOrderFor("down"),
      render: (_: unknown, record: SessionRow) => {
        const v = downOf(record);
        return v ? usd.format(v) : <Typography.Text type="secondary">—</Typography.Text>;
      },
    },
    {
      // A prior-policy balance the carrier folds into the down payment. NULL
      // (never seen) renders as an em dash; an observed 0 renders as $0.00,
      // because "panel seen, amount unreadable" is worth noticing.
      title: "Outstanding",
      dataIndex: "outstanding_balance",
      key: "outstanding",
      align: "right",
      sorter: true,
      sortOrder: sortOrderFor("outstanding"),
      render: (value: number | null) => value == null
        ? <Typography.Text type="secondary">—</Typography.Text>
        : <Typography.Text type="warning" strong>{usd.format(value)}</Typography.Text>,
    },
    {
      title: "Premium",
      key: "premium",
      align: "right",
      sorter: true,
      sortOrder: sortOrderFor("premium"),
      render: (_: unknown, record: SessionRow) => {
        const v = premiumOf(record);
        return v ? usd.format(v) : <Typography.Text type="secondary">—</Typography.Text>;
      },
    },
    {
      title: "Policy #",
      dataIndex: "policy_number",
      render: (policyNumber: string | null) => policyNumber
        ? <Typography.Text copyable={{ text: policyNumber }} style={{ fontFamily: "monospace", fontSize: token.fontSizeSM }}>{policyNumber}</Typography.Text>
        : <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: "Timing",
      key: "timing",
      render: (_: unknown, row: SessionRow) => {
        const created = dayjs(row.created_at!);
        const ended = row.ended_at ? dayjs(row.ended_at) : null;
        const isStopped = row.is_alive === "stopped";
        return (
          <Flex gap={4} wrap="wrap">
            {row.execution_ms != null && ended ? (
              <Tag color="green" style={{ margin: 0 }}>exec {fmtDur(row.execution_ms)}</Tag>
            ) : isStopped && ended ? (
              <Tag color="blue" style={{ margin: 0 }}>total {fmtDur(ended.diff(created))}</Tag>
            ) : !isStopped ? (
              <Tag color="processing" style={{ margin: 0 }}>running {fmtDur(dayjs().diff(created))}</Tag>
            ) : null}
          </Flex>
        );
      },
    },
    {
      title: "Created",
      dataIndex: "created_at",
      key: "created",
      sorter: true,
      sortOrder: sortOrderFor("created"),
      render: (ts: string | null) => ts ? dayjs(ts).fromNow() : "—",
    },
  ];

  const handleRow: TableProps<SessionRow>["onRow"] = (record) => ({
    onClick: () => navigate({
      to: "/$organizationId/$flowId/sessions/$sessionId",
      params: { organizationId, flowId, sessionId: record.id },
    }),
    style: { cursor: "pointer" },
  });

  return (
    <div>
      <Flex align="center" justify="space-between" wrap="wrap" gap={token.marginMD} style={{ marginBottom: token.marginMD }}>
        <Flex align="center" wrap="wrap" gap={token.marginMD} style={{ flex: 1, minWidth: 0 }}>
          <Typography.Title level={4} style={{ margin: 0, whiteSpace: "nowrap" }}>
            {qOrganization.organization?.name ?? "Organization"} — Sessions
          </Typography.Title>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search by name or session ID"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={{ maxWidth: 320 }}
          />
          <Button
            type={hasOutstandingOnly ? "primary" : "default"}
            onClick={() => navigate({
              search: (prev) => ({
                ...prev,
                has_outstanding: hasOutstandingOnly ? undefined : "true",
                page: undefined,
              }),
            })}
          >
            Outstanding balance
          </Button>
          <Button
            icon={<ClearOutlined />}
            disabled={!hasActiveFilters}
            onClick={() => navigate({ search: (prev) => ({ sortField: prev.sortField, sortOrder: prev.sortOrder }) })}
          >
            Clear filters
          </Button>
        </Flex>
        <Flex align="center" gap={token.marginSM}>
          {qSessions.query.isFetching ? (
            <Spin size="small" />
          ) : (
            <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              Updated {dayjs(qSessions.query.dataUpdatedAt).format("HH:mm:ss")}
            </Typography.Text>
          )}
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)}>
            Create Session
          </Button>
        </Flex>
      </Flex>

      <Modal
        title={
          <Flex align="center" gap={4}>
            <span>Create Session</span>
            {import.meta.env.DEV && (
              <>
                <Switch
                  checked={localBrowser}
                  onChange={(checked) => {
                    setLocalBrowser(checked);
                    localStorage.setItem("ihub:localBrowser", String(checked));
                  }}
                  checkedChildren={<LaptopOutlined />}
                  unCheckedChildren={<LaptopOutlined />}
                  size="small"
                />
                <Typography.Text type={localBrowser ? "warning" : "secondary"} style={{ fontSize: token.fontSizeSM }}>
                  {localBrowser ? "Local" : "Cloud Run"}
                </Typography.Text>
              </>
            )}
          </Flex>
        }
        open={createModalOpen}
        onCancel={() => { setCreateModalOpen(false); createForm.resetFields(); }}
        onOk={() => {
          const mode = createForm.getFieldValue("mode");
          if (mode === "generate-policy") {
            modal.confirm({
              title: "Generate Policy",
              content: "This will attempt a real purchase on the carrier portal. This costs money and issues an actual policy.",
              okText: "Yes, Generate Policy",
              okType: "danger",
              onOk: () => createForm.submit(),
            });
          } else {
            createForm.submit();
          }
        }}
        confirmLoading={mSessionCreate.mutation.isPending}
        width={640}
        style={{ maxWidth: "calc(100vw - 32px)" }}
        destroyOnHidden
      >
        <Select
          showSearch
          allowClear
          placeholder="Load from test profile..."
          options={profileOptions}
          filterOption={(input, option) => option?.searchText?.includes(input.toLowerCase()) ?? false}
          onChange={(profileId) => {
            if (!profileId) return;
            const profile = qProfiles.profiles.find((p) => p.id === profileId);
            if (!profile) return;
            createForm.setFieldsValue(flattenToFormValues({
              customerData: {
                firstName: profile.first_name ?? "",
                lastName: profile.last_name ?? "",
                dob: profile.date_of_birth ?? "",
                phone: profile.phone ?? "",
                email: profile.email ?? "",
                driversLicense: profile.drivers_license ?? "",
                licenseState: profile.license_state ?? undefined,
                gender: profile.gender ?? undefined,
                maritalStatus: profile.marital_status ?? undefined,
                address: {
                  street: profile.address_street ?? "",
                  city: profile.address_city ?? "",
                  state: profile.address_state ?? "",
                  zip: profile.address_zip ?? "",
                },
              },
              quoteDetails: {
                state: profile.rate_state ?? "",
                policyType: profile.policy_type ?? "Named Non-Owner",
              },
            }));
          }}
          style={{ width: "100%", marginBottom: token.marginSM }}
          size="small"
        />
        <App_SessionComposer
          composerMode="session"
          form={createForm}
          onFinish={handleCreateSession}
        />
      </Modal>
      <Table<SessionRow>
        dataSource={qSessions.sessions}
        columns={columns}
        rowKey="id"
        loading={qSessions.query.isLoading}
        onRow={handleRow}
        onChange={handleTableChange}
        scroll={{ x: "max-content" }}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: qSessions.total,
          showSizeChanger: true,
          showTotal: (total) => `~${total} sessions`,
        }}
        size="middle"
      />
    </div>
  );
}
